import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { dummyNotifications } from "../../../dummyData";
import notificationSound from "../../../assets/sounds/notification-sound.mp3";

// Create the notification context
export const NotificationContext = createContext();

// Create a custom hook to use the notification context
export const useNotifications = () => useContext(NotificationContext);

// Demo notification types for random generation
const notificationTypes = [
  {
    type: "order",
    title: "New Order Received",
    message: "Order #ORD-XXXX has been placed",
    link: "/orders/orderdetail/ord001",
  },
  {
    type: "payment",
    title: "Payment Received",
    message: "Payment of ₹XX,XXX received from Customer",
    link: "/receipt/view/rcpt001",
  },
  {
    type: "inventory",
    title: "Low Stock Alert",
    message: 'Product "XXXX" is running low on stock',
    link: "/product/inventory",
  },
  {
    type: "customer",
    title: "New Customer Registration",
    message: "XXXX has registered as a new customer",
    link: "/customer/viewcustomer",
  },
  {
    type: "request",
    title: "New Customer Request",
    message: "XXXX has requested addition of X new products",
    link: "/customer/request",
  },
];

// Placeholder product names for random generation
const productNames = [
  "Designer Party Wear Suit",
  "Anarkali Style Salwar Suit",
  "Chikankari Embroidered Suit",
  "Cotton Casual Wear",
  "Palazzo Suit Set",
  "Bridal Collection",
  "Indo-Western Fusion Wear",
];

// Placeholder customer names for random generation
const customerNames = [
  "Innovative Textiles",
  "Shanti Apparels",
  "Suman Collection",
  "Royal Garments",
  "Elegant Ethnic Wear",
  "Fashion Hub",
  "Trendy Styles Ltd.",
];

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(dummyNotifications);
  const [showPopup, setShowPopup] = useState(false);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPopupSilenced, setIsPopupSilenced] = useState(false);
  const notificationSoundRef = useRef(null);

  // Use a ref to track the latest muted state for async operations
  const isMutedRef = useRef(isMuted);

  // Keep the ref updated with the latest state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Initialize audio element on mount
  useEffect(() => {
    // Use the imported notification sound
    notificationSoundRef.current = new Audio(notificationSound);

    // Set up audio element
    notificationSoundRef.current.preload = "auto";
    notificationSoundRef.current.volume = 0.5;

    // Add event listeners for debugging
    notificationSoundRef.current.addEventListener("error", e => {
      console.error("Audio error:", e);
      // Log the error details
      if (e.target.error) {
        console.error("Error code:", e.target.error.code);
        console.error("Error message:", e.target.error.message);
      }
    });

    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.pause();
        notificationSoundRef.current = null;
      }
    };
  }, []);

  // Toggle mute state
  const toggleMute = () => {
    console.log("Toggling mute from", isMuted, "to", !isMuted);
    setIsMuted(prevMuted => {
      const newMuted = !prevMuted;
      // Update the ref immediately
      isMutedRef.current = newMuted;
      // Persist mute preference in localStorage
      localStorage.setItem("notificationsMuted", newMuted ? "true" : "false");
      return newMuted;
    });
  };

  // Toggle popup silence state
  const togglePopupSilence = () => {
    setIsPopupSilenced(prevSilenced => {
      const newSilenced = !prevSilenced;
      // Persist popup silence preference in localStorage
      localStorage.setItem(
        "notificationsPopupSilenced",
        newSilenced ? "true" : "false",
      );
      return newSilenced;
    });
  };

  // Initialize mute state from localStorage
  useEffect(() => {
    const mutedState = localStorage.getItem("notificationsMuted") === "true";
    setIsMuted(mutedState);
    // Update the ref immediately
    isMutedRef.current = mutedState;

    const popupSilencedState =
      localStorage.getItem("notificationsPopupSilenced") === "true";
    setIsPopupSilenced(popupSilencedState);
  }, []);

  // Function to play notification sound
  const playNotificationSound = () => {
    console.log(
      "Play notification sound called. isMuted =",
      isMuted,
      "isMutedRef =",
      isMutedRef.current,
    );

    // Check the ref value to ensure we have the latest state
    if (isMutedRef.current || !notificationSoundRef.current) {
      console.log("Not playing sound because muted =", isMutedRef.current);
      return;
    }

    try {
      console.log("Attempting to play notification sound");

      // Reset the audio to start
      notificationSoundRef.current.currentTime = 0;

      // Create a user interaction promise
      const playPromise = notificationSoundRef.current.play();

      // Handle async nature of play() method
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            console.log("Sound played successfully");
          })
          .catch(error => {
            // Auto-play was prevented
            console.error("Playback error:", error);

            // For demo purposes, we can try to work around auto-play restrictions
            // by playing a silent sound first, then the actual sound
            const silentSound = new Audio(
              "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABPgD///////////////////////////////////////////8AAAA8TEFNRTMuMTAwAQAAAAAAAAAAABSAJALBQgAAgAAAAT4AUijQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=",
            );
            silentSound.volume = 0.01;
            silentSound
              .play()
              .then(() => {
                notificationSoundRef.current
                  .play()
                  .then(() => console.log("Second attempt succeeded"))
                  .catch(e => console.error("Second attempt failed:", e));
              })
              .catch(e => console.error("Silent sound failed:", e));
          });
      }
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  };

  // Function to generate a random notification
  const generateRandomNotification = () => {
    // Generate random values
    const randomType =
      notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
    const randomOrderId = `ORD-${Math.floor(1000 + Math.random() * 9000)}`;
    const randomAmount = Math.floor(5000 + Math.random() * 50000);
    const randomProduct =
      productNames[Math.floor(Math.random() * productNames.length)];
    const randomCustomer =
      customerNames[Math.floor(Math.random() * customerNames.length)];
    const randomProductCount = Math.floor(1 + Math.random() * 10);

    // Create notification based on type
    let message = randomType.message;

    if (randomType.type === "order") {
      message = `Order #${randomOrderId} has been placed`;
    } else if (randomType.type === "payment") {
      message = `Payment of ₹${randomAmount.toLocaleString(
        "en-IN",
      )} received from ${randomCustomer}`;
    } else if (randomType.type === "inventory") {
      message = `Product "${randomProduct}" is running low on stock`;
    } else if (randomType.type === "customer") {
      message = `${randomCustomer} has registered as a new customer`;
    } else if (randomType.type === "request") {
      message = `${randomCustomer} has requested addition of ${randomProductCount} new products`;
    }

    return {
      id: `notif${Date.now()}`,
      type: randomType.type,
      title: randomType.title,
      message: message,
      isRead: false,
      timestamp: new Date().toISOString(),
      link: randomType.link,
    };
  };

  // Add a new notification
  const addNotification = (notification = null) => {
    const newNotification = notification || generateRandomNotification();
    console.log(
      "Adding notification. isMuted =",
      isMuted,
      "isMutedRef =",
      isMutedRef.current,
    );

    setNotifications(prev => [newNotification, ...prev]);

    // Only show popup if not silenced
    if (!isPopupSilenced) {
      setCurrentPopup(newNotification);
      setShowPopup(true);

      // Hide popup after 5 seconds
      setTimeout(() => {
        setShowPopup(false);
      }, 5000);
    }

    // Use the ref to check the latest muted state
    if (!isMutedRef.current) {
      console.log(
        "Calling playNotificationSound because isMutedRef =",
        isMutedRef.current,
      );
      playNotificationSound();
    } else {
      console.log("Skipping sound because isMutedRef =", isMutedRef.current);
    }
  };

  // Mark a notification as read
  const markAsRead = notificationId => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif,
      ),
    );
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
  };

  // Get unread count
  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  // Set up random notification generation for demo
  useEffect(() => {
    // Generate a random notification every 20-60 seconds
    const minTime = 20000;
    const maxTime = 60000;

    const getRandomDelay = () =>
      Math.floor(minTime + Math.random() * (maxTime - minTime));

    const scheduleNextNotification = () => {
      const delay = getRandomDelay();
      const timerId = setTimeout(() => {
        addNotification();
        scheduleNextNotification();
      }, delay);

      // Store the timer ID for cleanup
      return timerId;
    };

    const timerId = scheduleNextNotification();

    // Clean up on unmount
    return () => clearTimeout(timerId);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        showPopup,
        setShowPopup,
        currentPopup,
        unreadCount,
        isMuted,
        toggleMute,
        isPopupSilenced,
        togglePopupSilence,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};
